// src/v3/ui/_common/lib/http-client.ts

import { MILLISECONDS_PER_SECOND } from "@signals/shared";

function isApiEnvelope<T>(
  body: T,
): body is T & { data: T; error: unknown; metadata: unknown } {
  return (
    typeof body === "object" &&
    body !== null &&
    "data" in body &&
    "error" in body &&
    "metadata" in body
  );
}

export async function apiFetch<T = unknown>(
  url: string,
  options?: RequestInit & { timeoutMs?: number },
): Promise<T> {
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? 30_000;
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const { timeoutMs: _omit, ...fetchOptions } = options ?? {};
    const res = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(text || `Request failed: ${res.status}`);
    }
    const body: T = await res.json();
    return isApiEnvelope(body) ? body.data : body;
  } catch (error) {
    if (timedOut) {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / MILLISECONDS_PER_SECOND)} seconds.`);
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}
